import { useTranslation } from "react-i18next";
import type { EtfDetail } from "../../../api/services/etf.type";
import { Link } from "../../../router/LocaleLink";
import { Paths } from "../../../router/paths";
import screenerStyles from "../StockScreener/stockScreener.module.css";
import { EtfVerdictBadge } from "../EtfScreener/EtfVerdictBadge";
import { formatEtfPercent } from "../EtfScreener/etfFormat";
import etfStyles from "../EtfScreener/etfScreener.module.css";
import { EtfFacts } from "./EtfFacts";
import { EtfHoldings } from "./EtfHoldings";
import { EtfRulebooks } from "./EtfRulebooks";
import { EtfWeightSplit } from "./EtfWeightBars";
import styles from "./etfDetail.module.css";

const VERDICT_LABEL: Record<string, string> = {
  HALAL: "halal",
  DOUBTFUL: "doubtful",
  "NOT HALAL": "notHalal",
  UNQUALIFIED: "notRated",
};

/**
 * One fund, server-rendered (src/routes/etf.tsx). The headline verdict is
 * the Akinda fund screen's, always named, with coverage beside it; the four
 * index rulebooks follow as other standards' views. No financial ratios: a
 * fund has none (handover §12.2).
 */
export default function EtfDetailPage({ fund }: { fund: EtfDetail }) {
  const { t } = useTranslation();
  const p = "pages.etfDetail.";
  const verdictKey = fund.status ? VERDICT_LABEL[fund.status] : undefined;
  const statusLabel = verdictKey
    ? t(`pages.etfScreener.verdict.${verdictKey}`)
    : (fund.status ?? "—");

  return (
    <article className={screenerStyles.shell}>
      <div className={screenerStyles.inner}>
        <header className={screenerStyles.pageHeader}>
          <Link to={Paths.ETF_SCREENER} className={styles.backLink}>
            {t(`${p}backToScreener`)}
          </Link>
          <div className={styles.titleRow}>
            <h1 className={screenerStyles.pageTitle}>{fund.fundName ?? fund.ticker}</h1>
            <span className={styles.tickerChip}>{fund.ticker}</span>
            {fund.holdingsTotal === 1 && (
              <span className={etfStyles.singleBadge} title={t("pages.etfScreener.singleHoldingHint")}>
                {t("pages.etfScreener.singleHolding")}
              </span>
            )}
          </div>
          {fund.issuer && <p className={screenerStyles.pageSubtitle}>{fund.issuer}</p>}
          <p className={screenerStyles.pageIntro}>
            {t(`${p}intro`, {
              fundName: fund.fundName ?? fund.ticker,
              ticker: fund.ticker,
              statusLabel,
              coverage: formatEtfPercent(fund.coverageWeightPerc),
            })}
          </p>
        </header>

        <div className={styles.grid}>
          <section className={styles.card}>
            <h2 className={styles.cardTitle}>{t(`${p}verdict.title`)}</h2>
            <div className={styles.verdictRow}>
              <span className={styles.verdictLabel}>{t(`${p}verdict.fundScreen`)}</span>
              <EtfVerdictBadge status={fund.status} />
              <span className={styles.coverage}>
                {t("pages.etfScreener.columns.coverage")}:{" "}
                <strong>{formatEtfPercent(fund.coverageWeightPerc)}</strong>
              </span>
            </div>
            <EtfWeightSplit fund={fund} />
            <p className={styles.note}>{t("pages.etfScreener.seoContent.coverageParagraph")}</p>
          </section>

          <EtfFacts fund={fund} />
          <EtfRulebooks fund={fund} />
          <EtfHoldings ticker={fund.ticker} />
        </div>

        <p className={styles.note}>
          <Link to={Paths.SHARIAH_SCREENING_METHODOLOGY} className={styles.backLink}>
            {t(`${p}methodologyLink`)}
          </Link>
        </p>
      </div>
    </article>
  );
}
